/**
 * Editor-`api`-keyed slots for callbacks that cross a boundary React context cannot.
 *
 * The thinking widget renders through `nodeViewPortalProviderAPI`, so it is not a descendant of the
 * plugin's `contentComponent()`. The UX closures live in `RemixContentComponentInner`, inside
 * `@atlassian/malleable-ui-components`, which cannot import from this package. Both reach across via
 * `api`, the way `onRegisterAbortRequest` already bridges `registerAbortRequest` in `./remix-state`.
 *
 * Machine ownership and completion presentation belong to the retained lifecycle session; UX
 * handles belong to a content component that unmounts on terminal cleanup.
 */

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type {
	EditorRemixMachineLifecycleOwnershipCleanup,
	EditorRemixMachineLifecycleSession,
} from '@atlassian/malleable-ui-components/types';

import type { MalleableUiPlugin } from '../../malleableUiPluginType';

type EditorApi = ExtractInjectionAPI<MalleableUiPlugin> | undefined;

type ApiKeyedCallbackSlot<TValue> = {
	get: (api: EditorApi) => TValue | undefined;
	register: (api: EditorApi, value: TValue) => () => void;
};

/**
 * One value per editor instance, with cleanup that cannot outlive its own registration.
 *
 * The map holds a wrapper object so cleanup is guarded on *entry* identity. Guarding on the value
 * would not be enough: re-registering the same function object — a stable `useCallback` across a
 * remount — would make the stale cleanup indistinguishable from the current one, and unmounting the
 * old consumer would delete the new consumer's registration.
 *
 * `WeakMap`-keyed, so two editors on one page cannot see each other and nothing is retained once an
 * editor is collected.
 */
const createApiKeyedCallbackSlot = <TValue>(): ApiKeyedCallbackSlot<TValue> => {
	const entries = new WeakMap<object, { value: TValue }>();

	return {
		get: (api) => (api ? entries.get(api)?.value : undefined),
		register: (api, value) => {
			// No-op cleanup for an absent api keeps callers from branching, as `registerAbortRequest` does.
			if (!api) {
				return () => {};
			}

			const entry = { value };
			entries.set(api, entry);

			return () => {
				if (entries.get(api) === entry) {
					entries.delete(api);
				}
			};
		},
	};
};

/**
 * Announces that the Remix completion animation has finished.
 *
 * Argument-free by design: the host scopes the resulting state to its own lifecycle session, so a
 * sink invoked by a stale widget cannot describe a different flow.
 */
export type CapturedEditorRemixAnimationFinishedSink = () => boolean;

/**
 * A terminal outcome for the Remix experience trackers. An abort carries the flow it settles,
 * because the content component holds per-flow state the reason alone cannot be matched against.
 */
export type EditorRemixExperienceOutcome =
	| { error: Error; kind: 'failed' }
	| { kind: 'aborted'; reason: string; remixId: string };

/**
 * Whether the component settled the outcome a handler asked it to settle. `ignored` means this flow
 * already committed a different terminal outcome, so the handler's remaining teardown does not apply
 * either.
 */
export type EditorRemixExperienceSettlement = 'ignored' | 'settled';

/**
 * The editor UX a command handler needs but cannot reach, because these closures live in
 * `RemixContentComponentInner` and the handlers are mounted above it.
 *
 * Intent-shaped rather than raw closures: `settleExperience` describes the outcome instead of
 * exposing the experience tracker, so this seam does not take on the tracker's type and a handler
 * cannot settle half of it.
 */
export type EditorRemixUxHandles = {
	/** Fully closes the remix modal, unmounting the content component. */
	closeModal: () => void;
	/**
	 * Closes the ephemeral preview where this cohort's terminal cleanup owns that, and is a no-op
	 * where it does not — in edit mode with in-editor loading on, matching the legacy error path. The
	 * caller decides *that* the preview should close for a terminal outcome; the content component
	 * decides whether this cohort closes it.
	 */
	closeTerminalPreview: () => void;
	/**
	 * Marks the result ready and starts the local completion animation. Never writes ProseMirror
	 * lifecycle: the mirror already owns completion off the machine's `ready` phase.
	 */
	markReady: () => void;
	openPreview: () => void;
	/** Opens the Rovo side chat, so a failure is visible where the response is. */
	openSidebar: () => void;
	/** Cancellation-only cleanup, for a teardown that no terminal command settled. */
	settleCancellation: () => void;
	/**
	 * Settles the experience trackers for a terminal outcome. Returns `ignored` when this flow
	 * already committed a different terminal outcome, which the caller must treat as "my remaining
	 * teardown does not apply to this flow".
	 */
	settleExperience: (outcome: EditorRemixExperienceOutcome) => EditorRemixExperienceSettlement;
	/** `preserveRequest` suppresses only the abort of the in-flight AI request. */
	stopThinking: (options?: { preserveRequest?: boolean; reopenModal?: boolean }) => void;
};

const uxHandlesSlot = createApiKeyedCallbackSlot<EditorRemixUxHandles>();

export type EditorRemixLifecyclePresentation = Readonly<{
	animationFinished: boolean;
	authority: 'active' | 'terminal-latched' | undefined;
	remixId: string | undefined;
	token: symbol | undefined;
}>;

/** Runs a captured treatment cleanup only while its session is still current. */
export type CapturedEditorRemixTerminalCleanupSink = (cleanup: () => void) => boolean;

/** Cancels a Remix whose tracked source or target changed. */
export type CapturedEditorRemixMutationCancellationSink = () => boolean;

type LifecycleSession = {
	animationFinished: boolean;
	animationFinishedSink: CapturedEditorRemixAnimationFinishedSink;
	authority: 'active' | 'terminal-latched';
	mutationCancellationEnabled: boolean;
	mutationInvalidationPending: boolean;
	presentation: EditorRemixLifecyclePresentation;
	remixId: string;
	token: symbol;
};

const lifecycleSessions = new WeakMap<object, LifecycleSession>();
const presentationListeners = new WeakMap<object, Set<() => void>>();
const mutationCancellationSinks = new WeakMap<
	object,
	{ session: LifecycleSession; sink: CapturedEditorRemixMutationCancellationSink }
>();
const EMPTY_PRESENTATION: EditorRemixLifecyclePresentation = Object.freeze({
	animationFinished: false,
	authority: undefined,
	remixId: undefined,
	token: undefined,
});

const notifyPresentation = (api: object): void => {
	presentationListeners.get(api)?.forEach((listener) => {
		try {
			listener();
		} catch {
			// A presentation subscriber cannot prevent lifecycle authority from latching or releasing.
		}
	});
};

/**
 * Returns the current session-bound completion sink. The session identity is captured now, so a
 * portal callback held by a replaced flow cannot complete its replacement.
 */
export const getEditorRemixAnimationFinishedSink = (api: EditorApi): (() => void) | undefined => {
	const captured = captureEditorRemixAnimationFinishedSink(api);
	return captured
		? () => {
				captured();
			}
		: undefined;
};

/** Captures the current treatment identity for a portal callback before it is rendered. */
export const captureEditorRemixAnimationFinishedSink = (
	api: EditorApi,
): CapturedEditorRemixAnimationFinishedSink | undefined => {
	const session = api ? lifecycleSessions.get(api) : undefined;
	return session?.authority === 'active' ? session.animationFinishedSink : undefined;
};

/**
 * Captures the current treatment session for terminal UX cleanup. The caller must invoke this
 * before aborting, toggling, or dispatching any shared cleanup command; stale A callbacks then
 * become no-ops before they can mutate replacement B.
 */
export const captureEditorRemixTerminalCleanupSink = (
	api: EditorApi,
): CapturedEditorRemixTerminalCleanupSink | undefined => {
	if (!api) {
		return undefined;
	}
	const capturedApi = api;
	const captured = lifecycleSessions.get(capturedApi);
	if (!captured) {
		return undefined;
	}

	return (cleanup) => {
		if (lifecycleSessions.get(capturedApi) !== captured) {
			return false;
		}
		try {
			cleanup();
		} finally {
			if (captured.authority === 'terminal-latched') {
				releaseEditorRemixLifecycleAuthority(capturedApi, captured.token);
			}
		}
		return true;
	};
};

/** Registers the content component's UX handles. Returns its cleanup. */
export const registerEditorRemixUxHandles = (
	api: EditorApi,
	handles: EditorRemixUxHandles,
): (() => void) => uxHandlesSlot.register(api, handles);

/** Registers the active session's source/target cancellation path. */
export const registerEditorRemixMutationCancellationSink = (
	api: EditorApi,
	sink: CapturedEditorRemixMutationCancellationSink,
): (() => void) => {
	const session = api ? lifecycleSessions.get(api) : undefined;
	if (!api || !session || session.authority !== 'active') {
		return () => {};
	}

	const entry = { session, sink };
	mutationCancellationSinks.set(api, entry);
	return () => {
		if (mutationCancellationSinks.get(api) === entry) {
			mutationCancellationSinks.delete(api);
		}
	};
};

/** Captures a cancellation sink fenced to the current retained session. */
export const captureEditorRemixMutationCancellationSink = (
	api: EditorApi,
): CapturedEditorRemixMutationCancellationSink | undefined => {
	if (!api) {
		return undefined;
	}
	const entry = mutationCancellationSinks.get(api);
	if (
		!entry ||
		lifecycleSessions.get(api) !== entry.session ||
		entry.session.authority !== 'active' ||
		!entry.session.mutationCancellationEnabled
	) {
		return undefined;
	}

	return () => {
		if (lifecycleSessions.get(api) !== entry.session || entry.session.authority !== 'active') {
			return false;
		}
		entry.session.mutationInvalidationPending = true;
		const cancelled = entry.sink();
		// A refused cancellation must not leak presentation state.
		if (!cancelled && lifecycleSessions.get(api) === entry.session) {
			entry.session.mutationInvalidationPending = false;
		}
		return cancelled;
	};
};

/** Disarms mutation cancellation before Remix replaces its own tracked range. */
export const disableEditorRemixMutationCancellationSink = (api: EditorApi): boolean => {
	const session = api ? lifecycleSessions.get(api) : undefined;
	if (!api || !session || session.authority !== 'active') {
		return false;
	}
	session.mutationCancellationEnabled = false;
	return true;
};

/** Consumes source/target invalidation presentation for the matching command. */
export const consumeEditorRemixMutationInvalidation = (
	api: EditorApi,
	remixId: string,
): boolean => {
	const session = api ? lifecycleSessions.get(api) : undefined;
	if (!session || session.remixId !== remixId || !session.mutationInvalidationPending) {
		return false;
	}
	session.mutationInvalidationPending = false;
	return true;
};

/**
 * `undefined` while the content component is unmounted, which is the normal state after terminal
 * cleanup — an absent value means "no UX to drive", not a failure.
 */
export const getEditorRemixUxHandles = (api: EditorApi): EditorRemixUxHandles | undefined =>
	uxHandlesSlot.get(api);

/**
 * Establishes ownership at the retain boundary, with the same token that guards lifecycle release.
 * It also resets the session-scoped completion presentation synchronously.
 */
export const registerEditorRemixMachineLifecycleOwnership = (
	api: EditorApi,
	options: EditorRemixMachineLifecycleSession,
): EditorRemixMachineLifecycleOwnershipCleanup => {
	if (!api) {
		return () => {};
	}
	const { remixId, token } = options;
	const session: LifecycleSession = {
		animationFinished: false,
		animationFinishedSink: () => {
			// The closure captures the session identity at registration time. A portal callback retained
			// by A cannot complete a replacement B, even if B is now the registry's current session.
			const currentSession = api ? lifecycleSessions.get(api) : undefined;
			if (
				!api ||
				currentSession !== session ||
				currentSession?.token !== token ||
				currentSession.authority !== 'active'
			) {
				return false;
			}
			markEditorRemixAnimationFinished(api, token);
			return true;
		},
		remixId,
		token,
		authority: 'active',
		mutationCancellationEnabled: true,
		mutationInvalidationPending: false,
		presentation: {
			animationFinished: false,
			authority: 'active',
			remixId,
			token,
		},
	};
	lifecycleSessions.set(api, session);
	notifyPresentation(api);
	const cleanup: EditorRemixMachineLifecycleOwnershipCleanup = Object.assign(
		() => {
			if (lifecycleSessions.get(api) !== session || session.authority === 'terminal-latched') {
				return;
			}
			lifecycleSessions.delete(api);
			notifyPresentation(api);
		},
		{
			latchTerminalAuthority: () => {
				if (lifecycleSessions.get(api) !== session) {
					return;
				}
				session.authority = 'terminal-latched';
				session.presentation = {
					...session.presentation,
					authority: 'terminal-latched',
				};
				notifyPresentation(api);
			},
			settleTerminalAuthority: () => releaseEditorRemixLifecycleAuthority(api, token),
		},
	);
	return cleanup;
};

/** True only while a machine session has registered ownership for this editor. */
export const isEditorRemixLifecycleMachineOwned = (api: EditorApi): boolean =>
	Boolean(api && lifecycleSessions.get(api)?.authority === 'active');

/** True while this session may still fence legacy lifecycle writes. */
export const isEditorRemixLifecycleWriteAuthorityHeld = (api: EditorApi): boolean =>
	Boolean(api && lifecycleSessions.get(api)?.token);

/** Releases only the matching terminal authority lease; stale cleanup cannot affect a replacement. */
function releaseEditorRemixLifecycleAuthority(api: EditorApi, token: symbol | undefined): boolean {
	if (!api || token === undefined) {
		return false;
	}
	const session = lifecycleSessions.get(api);
	if (session?.token !== token || session.authority !== 'terminal-latched') {
		return false;
	}
	lifecycleSessions.delete(api);
	notifyPresentation(api);
	return true;
}

export const getEditorRemixLifecyclePresentation = (
	api: EditorApi,
): EditorRemixLifecyclePresentation => {
	const session = api ? lifecycleSessions.get(api) : undefined;
	return session?.presentation ?? EMPTY_PRESENTATION;
};

export const subscribeToEditorRemixLifecyclePresentation = (
	api: EditorApi,
	listener: () => void,
): (() => void) => {
	if (!api) {
		return () => {};
	}
	const listeners = presentationListeners.get(api) ?? new Set<() => void>();
	presentationListeners.set(api, listeners);
	listeners.add(listener);
	return () => listeners.delete(listener);
};

export const markEditorRemixAnimationFinished = (api: EditorApi, token: symbol): void => {
	const session = api ? lifecycleSessions.get(api) : undefined;
	if (
		!api ||
		!session ||
		session.token !== token ||
		session.authority !== 'active' ||
		session.animationFinished
	) {
		return;
	}
	session.animationFinished = true;
	session.presentation = {
		...session.presentation,
		animationFinished: true,
	};
	notifyPresentation(api);
};
